import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    LISTING_PAGE_PARAM_TAB_OVERVIEW,
    LISTING_PAGE_PARAM_TYPE_DRAFT_INTERRUPTED,
    LISTING_PAGE_PARAM_TYPE_EDIT,
    LISTING_PAGE_PARAM_TYPE_EDIT_AND_REDIRECT,
    PHOTOS,
} from '../EditListingWizard/config';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { createResourceLocatorString } from '../../util/routes';
import { getListingNavParams } from '../../util/listings';
import { duplicateListingAssets } from '../../util/api';
import routeConfiguration from '../../routeConfiguration';
import {
    closeListing,
    discardDraftListing,
    openListing,
} from '../../containers/ManageListingsPage/ManageListingsPage.duck';
import { requestCreateListingDraft } from '../../containers/EditListingPage/EditListingPage.duck';
import { getBoostingPath, getDuplicateListingParams } from './helpers';

/**
 * Navigation and listing actions available from a manage listing card
 * @param {Object} listing - ensured own listing
 */
export const useListingCardActions = listing => {
    const history = useHistory();
    const dispatch = useDispatch();

    const {
        id: { uuid: listingId },
        attributes: { state, publicData },
    } = listing;
    const { params, name } = getListingNavParams(listing);
    const { country } = publicData.addressDetails || {};

    const isDraft = state === LISTING_STATE_DRAFT;

    const navigate = (pageName, pageParams) =>
        history.push(createResourceLocatorString(pageName, routeConfiguration(), pageParams, {}));

    const navigateToListingPage = () => navigate(name, params);

    const navigateToEditListingPage = () =>
        navigate('EditListingPage', {
            ...params,
            type: isDraft
                ? LISTING_PAGE_PARAM_TYPE_DRAFT_INTERRUPTED
                : LISTING_PAGE_PARAM_TYPE_EDIT,
            tab: LISTING_PAGE_PARAM_TAB_OVERVIEW,
        });

    const navigateToPackagesPage = () =>
        navigate('EditListingPage', {
            ...params,
            type: LISTING_PAGE_PARAM_TYPE_EDIT_AND_REDIRECT,
            tab: getBoostingPath(country),
        });

    const navigateToImagesPage = () =>
        navigate('EditListingPage', {
            ...params,
            type: LISTING_PAGE_PARAM_TYPE_EDIT,
            tab: PHOTOS,
        });

    const duplicateListing = async () => {
        try {
            const response = await dispatch(
                requestCreateListingDraft(getDuplicateListingParams(listing))
            );
            const listingDuplicate = response.data.data;
            const { uuid } = listingDuplicate.id;

            await duplicateListingAssets(uuid);

            navigate('EditListingPage', {
                slug: getListingNavParams(listingDuplicate).params.slug,
                id: uuid,
                type: LISTING_PAGE_PARAM_TYPE_DRAFT_INTERRUPTED,
                tab: LISTING_PAGE_PARAM_TAB_OVERVIEW,
            });
        } catch (e) {
            // do nothing
        }
    };

    return {
        navigateToListingPage,
        navigateToEditListingPage,
        navigateToPackagesPage,
        navigateToImagesPage,
        duplicateListing,
        reactivateListing: () => dispatch(openListing(listingId)),
        pauseListing: values => dispatch(closeListing(listingId, values)),
        discardDraft: () => dispatch(discardDraftListing(listingId)),
    };
};
